/**
 * Channel detail → the channel's REAL campaigns + their today figures + the channel
 * ad-spend totals, from the Casino Ad Service and the Owners Hub Stats API.
 *
 * Three sources, resolved together for one coordinated reveal:
 *  - **Casino Ad Service** `GET /campaigns?tenant_id`, filtered to the channel's
 *    `network` (web / app / crypto / video). The card image is the list row's
 *    `master_key_art` (no per-campaign detail fetch); each campaign's today + yesterday
 *    spend (`performance.totals.cost`) is summed across the channel for the Daily Spend
 *    tile / CPA card / budget figure.
 *  - **Owners Hub Stats** `daily_affiliate_activity`, aggregated on the `campaign`
 *    dimension and FILTERED to each campaign's id (one call per campaign), for the today
 *    Registrations / FTDs / Avg FTD Value tiles. The filtered shape is robust by
 *    construction (the value we send is echoed back in `aggregate_on.campaign`, so
 *    `toBucketsFor` always matches); an UNfiltered grouping is NOT used because the
 *    backend currently returns a single empty-string `campaign` group, which would
 *    silently zero every campaign (verified live, see `docs/api.owners-hub.stats.md`).
 *
 * The today window is the UTC day ({@link homeWindow}, the same basis the grid + CPA use)
 * so the ad-spend `cost` and the FTD buckets cover the same calendar day. Today re-polls
 * ~every 5 min in place (stale-while-revalidate — the cards aren't re-skeletoned on the
 * tick, only on a real brand / channel / day change). The ad-service reports only created
 * campaigns; a per-campaign cost / stat with no data counts as `$0` / `0`, not a channel
 * failure. Contract: `docs/api.owners-hub.acquisition-campaigns.md`.
 */
import React from 'react';

import { useAppDispatch } from '@/store';
import { fetchStatsDataSetHistory } from '@/store/slices/ownerHubStats';

import type { ChannelCampaign, ChannelId } from '../../mock-data/types';
import { homeWindow } from '../home/stats-mapping';
import { type Bucket, toBucketsFor } from '../home/stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';
import {
  type AdCampaignSummary,
  getAdCampaignPerformance,
  listAdCampaigns,
} from '../acquisition/api/casino-ad-service';
import { buildCampaignTileStats, CHANNEL_NETWORK, toChannelCampaign } from './channel-network';

const DATA_SET = 'daily_affiliate_activity';
const DIMENSION = 'campaign';
const AGGREGATE_ON = ['campaign'];

const pad = (n: number): string => String(n).padStart(2, '0');
/** A `YYYY-MM-DD` UTC date shifted by `days` (the ad-service windows are inclusive UTC days). */
const utcShiftYmd = (ymd: string, days: number): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d) + days * 86_400_000);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

/** One channel campaign enriched from both sources: its summary (carrying the card image
 *  as `master_key_art`), its today / yesterday spend and today's filtered stat buckets
 *  (+ whether that stats call failed). */
type AdRow = {
  summary: AdCampaignSummary;
  costToday: number;
  costYesterday: number;
  statsBuckets: Bucket[];
  statsErrored: boolean;
};

type AdState = {
  rows: AdRow[];
  channelCostToday: number | null;
  channelCostYesterday: number | null;
  errored: boolean;
  loading: boolean;
};

const AD_LOADING: AdState = {
  rows: [],
  channelCostToday: null,
  channelCostYesterday: null,
  errored: false,
  loading: true,
};

export type ChannelCampaignsResult = {
  items: ChannelCampaign[];
  /** Channel ad-spend today / yesterday (whole currency) — feeds the Daily Spend tile,
   *  the CPA card and the budget "Spent" figure. `null` until the spend resolves. */
  channelCostToday: number | null;
  channelCostYesterday: number | null;
  /** The ad-spend request failed (the cost-backed figures then read `N/A`). */
  costErrored: boolean;
  /** True until the campaigns list + their spend + thumbnails + tile stats first resolve. */
  loading: boolean;
};

/**
 * @param channelId The channel page id (drives the `network` filter); `undefined` keeps
 *        the section loading.
 * @param tenantId The active brand's tenant id (`displayBrand?.tenant_id`) for the Ad
 *        Service; `undefined` keeps it loading until a brand resolves.
 * @param casinoInstanceId Active brand id (`displayBrand?.id`) for the per-campaign Stats.
 */
export const useChannelCampaigns = (
  channelId: ChannelId | undefined,
  tenantId: string | undefined,
  casinoInstanceId: string | undefined,
): ChannelCampaignsResult => {
  const dispatch = useAppDispatch();
  const network = channelId ? CHANNEL_NETWORK[channelId] : undefined;

  // Today re-polls ~every 5 min; the tick rolls the window over at UTC midnight (the clock
  // read is a hidden time dep). The window is the UTC day — the same basis the grid + CPA
  // FTD buckets use — so the ad-spend `cost` and the FTDs cover the same calendar day.
  const tick = useRefreshTick(true, LIVE_REFRESH_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow('TODAY', Date.now()), [tick]);
  const adToday = window.startDate.slice(0, 10);
  const adYesterday = utcShiftYmd(adToday, -1);

  const [ad, setAd] = React.useState<AdState>(AD_LOADING);
  // The fetch re-runs on the live tick too, but the cards are re-skeletoned ONLY on a real
  // key change (brand / channel / day) — the tick refreshes in place (stale-while-revalidate).
  const keyRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!tenantId || !network || !casinoInstanceId) {
      setAd(AD_LOADING);
      keyRef.current = '';
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${tenantId}|${network}|${casinoInstanceId}|${adToday}`;
    const freshKey = key !== keyRef.current;
    keyRef.current = key;
    if (freshKey) setAd((prev) => ({ ...prev, loading: true }));

    const costOf = (id: string, date: string): Promise<number> =>
      getAdCampaignPerformance(id, tenantId, { breakdown: 'summary', dateFrom: date, dateTo: date }, signal)
        .then((perf) => perf.totals.cost ?? 0)
        .catch(() => 0);

    // ONE filtered Stats call per campaign: the campaign id we send is echoed in
    // `aggregate_on.campaign`, so `toBucketsFor` matches it back deterministically.
    const statsOf = (id: string): Promise<{ buckets: Bucket[]; errored: boolean }> =>
      dispatch(
        fetchStatsDataSetHistory({
          casinoInstanceId,
          dataSet: DATA_SET,
          startDate: window.startDate,
          endDate: window.endDate,
          intervalSlice: window.intervalSlice,
          aggregateOn: [...AGGREGATE_ON],
          filter: { [DIMENSION]: id },
        }),
      )
        .unwrap()
        .then((entry) => ({ buckets: toBucketsFor(entry.data, DIMENSION, id), errored: false }))
        .catch(() => ({ buckets: [] as Bucket[], errored: true }));

    listAdCampaigns({ tenantId, pageSize: 100 }, signal)
      .then(async (list) => {
        const summaries = list.campaigns.filter((campaign) => campaign.network === network);
        const rows = await Promise.all(
          summaries.map(async (summary): Promise<AdRow> => {
            const [costToday, costYesterday, stats] = await Promise.all([
              costOf(summary.campaign_id, adToday),
              costOf(summary.campaign_id, adYesterday),
              statsOf(summary.campaign_id),
            ]);
            return { summary, costToday, costYesterday, statsBuckets: stats.buckets, statsErrored: stats.errored };
          }),
        );
        if (signal.aborted) return;
        setAd({
          rows,
          channelCostToday: rows.reduce((sum, row) => sum + row.costToday, 0),
          channelCostYesterday: rows.reduce((sum, row) => sum + row.costYesterday, 0),
          errored: false,
          loading: false,
        });
      })
      .catch(() => {
        // Ignore the abort the cleanup triggers; a real list failure flips the cost-backed
        // figures to `N/A` and shows no cards.
        if (!signal.aborted) {
          setAd({ rows: [], channelCostToday: null, channelCostYesterday: null, errored: true, loading: false });
        }
      });

    return () => controller.abort();
    // adYesterday is a pure derivation of adToday (same value until the day rolls), so it
    // adds no extra runs; listed to satisfy exhaustive-deps.
  }, [dispatch, tenantId, network, casinoInstanceId, adToday, adYesterday, window.startDate, window.endDate, window.intervalSlice, tick]);

  // A campaign the studio never shows (Failed / Deleted / an unrecognised state) maps to
  // null and is left off the channel. Its spend still counts toward the channel totals
  // above: the Daily Spend tile is the channel's money, not a sum of the cards.
  const items = React.useMemo<ChannelCampaign[]>(
    () =>
      ad.rows
        .map((row) =>
          toChannelCampaign(
            row.summary,
            undefined,
            buildCampaignTileStats(row.costToday, row.statsBuckets, row.statsErrored),
          ),
        )
        .filter((item): item is ChannelCampaign => item != null),
    [ad.rows],
  );

  return {
    items,
    channelCostToday: ad.channelCostToday,
    channelCostYesterday: ad.channelCostYesterday,
    costErrored: ad.errored,
    loading: ad.loading,
  };
};

export default useChannelCampaigns;
